import React, { useCallback, useState } from 'react'
import { Images } from '@/assets/configs'
import { EmtFileDropZone, EmtFileDropZoneProps } from 'emt-components-v2'
import { SxProps } from '@mui/material/styles'
import { useTranslation } from 'next-i18next'
import CropperModal from '@/components/Profile/Modals/CropperModal'

// FileDropZone.tsx
type FileDropZoneProps = Omit<EmtFileDropZoneProps, 'cloud1' | 'cloud2' | 'illustration' | 'errorTranslate'> & {
  isOwner: boolean
  overrideDropZoneBox?: SxProps
  dark?: boolean
  activeDeleteButton?: boolean
  /** keep false so we don't use the inner (legacy) rotate/crop */
  activeRotation?: boolean
  overrideIllustration?: string

  /** if the parent wants to override click behavior, we still allow it */
  overrideOpenImageDialog?: (open: () => void) => void

  /** enable this wrapper cropper (default: true) */
  enableCropper?: boolean

  onDelete?: () => void

  /** NEW: fired only after confirm in CropperModal */
  onCropped?: (base64: string) => void
}

const FileDropZone: React.FC<FileDropZoneProps> = ({
  isOwner,
  dark = false,
  maxFiles,
  onFileReject,
  overrideIllustration,
  overrideOpenImageDialog,
  enableCropper = true,
  // default to false so only this wrapper handles cropping/rotation
  activeRotation = false,
  onDrop, // legacy: still available, but NOT used to set value when cropping
  preview, // controlled by parent
  onDelete,
  onCropped, // <-- NEW
  ...rest
}) => {
  const { t } = useTranslation('common')

  const illustration = {
    owner: dark ? Images.FileDropZone.dark.Owner : Images.FileDropZone.Owner,
    borrower: dark ? Images.FileDropZone.dark.Borrower : Images.FileDropZone.Borrower,
  }

  const handleFileRejections = (fileRejections: any[]) => {
    onFileReject?.(fileRejections)
  }

  const [cropDialog, setCropDialog] = useState<{ open: boolean; src: string | null; tempSrc: string | null }>({
    open: false,
    src: null,
    tempSrc: null,
  })

  // Intercept inner drop:
  const handleInnerDrop = useCallback(
    (file: string | ArrayBuffer | null) => {
      if (!enableCropper) {
        onDrop?.(file) // legacy flow (no cropper)
        return
      }

      if (file === null) {
        onDelete?.()
        setCropDialog({ open: false, src: null, tempSrc: null })
        return
      }

      if (typeof file === 'string') {
        // @ts-expect-error
        setCropDialog({ open: true, src: preview ?? null, tempSrc: file })
        return
      }

      // ArrayBuffer case: ignore (or convert) until user confirms crop
    },
    [enableCropper, onDelete, onDrop, preview],
  )

  const handleCropComplete = useCallback(
    (base64: string) => {
      onCropped?.(base64) // parent updates preview/value here
      onDrop?.(base64) // optional legacy callback; avoid state writes in it
      setCropDialog({ open: false, src: null, tempSrc: null })
    },
    [onCropped, onDrop],
  )

  const handleCropClose = useCallback(() => {
    // Just close – NO callbacks, NO parent updates
    setCropDialog({ open: false, src: null, tempSrc: null })
  }, [])

  return (
    <>
      <EmtFileDropZone
        onFileReject={handleFileRejections}
        cloud1={Images.Cloud1}
        cloud2={Images.Cloud2}
        illustration={
          overrideIllustration ? overrideIllustration : isOwner ? illustration.owner : illustration.borrower
        }
        errorTranslate={{
          'file-invalid-type': t('fileDropZone.errors.file-invalid-type'),
          'file-image-invalid-type': t('fileDropZone.errors.file-image-invalid-type'),
          'file-too-large': t('fileDropZone.errors.file-too-large'),
          'file-too-small': t('fileDropZone.errors.file-too-small'),
          'too-many-files': t('fileDropZone.errors.too-many-files', { count: maxFiles }),
        }}
        onDrop={handleInnerDrop}
        activeRotation={activeRotation}
        overrideOpenImageDialog={overrideOpenImageDialog}
        preview={preview}
        controlledPreview
        maxFiles={maxFiles}
        {...rest}
      />

      {enableCropper && (
        <CropperModal
          open={cropDialog.open}
          imageSrc={cropDialog.tempSrc}
          onClose={handleCropClose}
          onComplete={handleCropComplete}
        />
      )}
    </>
  )
}

export default FileDropZone
